import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar, Check, Download, Loader2, Phone, Search, User, Volume2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import CdrStagingRecordsList from "@/components/cdr/CdrStagingRecordsList";
import useCdrStagingBasket from "@/hooks/useCdrStagingBasket";
import { downloadRecordingFromUrl, downloadRecordingViaPanel, isRecordingNotFoundMessage } from "@/utils/recordingDownload";
import { buildPublicRecordingUrl } from "@/utils/publicRecordingUrl";
import { normalizeTurkishPhone } from "@/utils/callPartyHelpers";
import TranscriptAudioPlayer from "@/components/common/TranscriptAudioPlayer";
import { recordingFilenameFromUrl } from "@/utils/tlRecordingReportHelpers";

export const formatCallDuration = (seconds) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return "—";
  const mins = Math.floor(value / 60);
  const secs = Math.floor(value % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const CdrRecordingPicker = ({
  apiBase, open, onClose, onSelect, agentId, reportDate, agents = [], isDark, fieldLabel = "", basketOnly = false,
}) => {
  const { items: stagedItems } = useCdrStagingBasket();
  const [viewMode, setViewMode] = useState("basket");
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [items, setItems] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [pickerDownloading, setPickerDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhone("");
    setItems([]);
    setSelectedRow(null);
    setSelectedAgentId(agentId ? String(agentId) : "");
    setStartDate(reportDate || "");
    setEndDate(reportDate || "");
    setViewMode(basketOnly || stagedItems.length > 0 ? "basket" : "search");
  }, [open, agentId, reportDate, stagedItems.length, basketOnly]);

  const search = useCallback(async () => {
    const normalizedPhone = normalizeTurkishPhone(phone.trim());
    const hasDate = Boolean(startDate || endDate);
    const hasAgent = Boolean(selectedAgentId);

    if (!normalizedPhone) {
      if (!hasDate && !hasAgent) {
        toast.warning("Numara girin veya agent/tarih seçip arayın");
        return;
      }
    }

    setLoading(true);
    setSelectedRow(null);
    try {
      const params = { page: 1, limit: 50 };
      if (selectedAgentId) params.agent_id = selectedAgentId;
      if (normalizedPhone) params.numara = normalizedPhone;

      const rangeStart = startDate || endDate;
      const rangeEnd = endDate || startDate;
      if (rangeStart) params.start_date = `${rangeStart} 00:00:00`;
      if (rangeEnd) params.end_date = `${rangeEnd} 23:59:59`;

      const res = await axios.get(`${apiBase}/cdr`, { params, timeout: 60000 });
      const body = res.data;
      let rows = Array.isArray(body) ? body : (body?.data || body?.records || []);

      if (rows.length === 0 && normalizedPhone && (rangeStart || rangeEnd)) {
        const wideRes = await axios.get(`${apiBase}/cdr`, {
          params: { page: 1, limit: 50, numara: normalizedPhone },
          timeout: 60000,
        });
        const wideBody = wideRes.data;
        rows = Array.isArray(wideBody) ? wideBody : (wideBody?.data || wideBody?.records || []);
        if (rows.length > 0) toast.info("Tarih aralığında bulunamadı; tüm tarihlerde arandı");
      }

      setItems(rows);
      if (rows.length === 0) toast.warning("Kayıt bulunamadı");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Çağrılar yüklenemedi");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, endDate, phone, selectedAgentId, startDate]);

  const handleConfirm = () => {
    if (!selectedRow?.recording) {
      toast.warning("Önce ses kaydı olan bir çağrı seçin");
      return;
    }
    const shareUrl = buildPublicRecordingUrl(selectedRow.id) || selectedRow.recording;
    onSelect(shareUrl);
    onClose();
    toast.success("Ses kaydı URL alanına eklendi");
  };

  const handlePickerDownload = async () => {
    if (!selectedRow?.recording || pickerDownloading) return;
    setPickerDownloading(true);
    try {
      if (selectedRow.id) {
        await downloadRecordingViaPanel({ apiBase, cdrId: selectedRow.id });
      } else {
        await downloadRecordingFromUrl({
          apiBase,
          recordingUrl: selectedRow.recording,
          filename: recordingFilenameFromUrl(selectedRow.recording),
        });
      }
      toast.success("Ses kaydı indirildi");
    } catch (err) {
      const msg = err.message || "Ses kaydı indirilemedi";
      if (isRecordingNotFoundMessage(msg)) toast.warning(msg);
      else toast.error(msg);
    } finally {
      setPickerDownloading(false);
    }
  };

  const inputClass = isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-gray-200";
  const muted = isDark ? "text-slate-400" : "text-gray-500";
  const filterCard = isDark ? "border-slate-700 bg-slate-950/50" : "border-gray-200 bg-gray-50/80";
  const tabBtn = (active) => (
    active
      ? (isDark ? "bg-violet-600/20 text-violet-200 border-violet-500/40" : "bg-violet-100 text-violet-700 border-violet-300")
      : (isDark ? "text-slate-400 border-slate-700 hover:bg-slate-800" : "text-gray-600 border-gray-200 hover:bg-gray-50")
  );
  const resultCard = (active) => (
    active
      ? (isDark ? "border-violet-500/60 bg-violet-500/10 ring-1 ring-violet-500/40" : "border-violet-400 bg-violet-50 ring-1 ring-violet-200")
      : (isDark ? "border-slate-700 bg-slate-950/40 hover:border-slate-600" : "border-gray-200 bg-white hover:border-gray-300")
  );
  const fieldHint = fieldLabel ? ` · ${fieldLabel}` : "";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0 ${isDark ? "bg-slate-900 border-slate-700 text-white" : ""}`}>
        <DialogHeader className="px-5 pt-5 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Volume2 className="w-5 h-5 text-violet-500" />
            Çağrı seç — ses URL&apos;sini forma ekle{fieldHint}
          </DialogTitle>
          <p className={`text-xs mt-1 ${muted}`}>
            {basketOnly || stagedItems.length > 0
              ? "CDR sepetindeki kayıtlardan seçin. Ses ikonuna basarak URL'yi ilgili alana ekleyebilirsiniz."
              : "Agent ve tarih ile listeleyin, numara ile daraltın, sesi dinledikten sonra URL'yi forma ekleyin."}
          </p>
          {!basketOnly && stagedItems.length > 0 && (
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={() => { setViewMode("basket"); setSelectedRow(null); }}
                className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${tabBtn(viewMode === "basket")}`}
              >
                CDR Sepeti ({stagedItems.length})
              </button>
              <button
                type="button"
                onClick={() => { setViewMode("search"); setSelectedRow(null); }}
                className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${tabBtn(viewMode === "search")}`}
              >
                Yeni Ara
              </button>
            </div>
          )}
        </DialogHeader>

        {(basketOnly || (viewMode === "basket" && stagedItems.length > 0)) ? (
          <div className="flex-1 overflow-y-auto min-h-0 px-5 py-3">
            <div className={`overflow-hidden rounded-lg border ${isDark ? "border-slate-800" : "border-gray-200"}`}>
              <CdrStagingRecordsList
                items={stagedItems}
                isDark={isDark}
                labelClass={muted}
                textClass={isDark ? "text-white" : "text-gray-900"}
                selectedId={selectedRow?.id ?? null}
                onSelect={setSelectedRow}
                selectable
                onPlay={(url, row) => setSelectedRow(row)}
                onApplyRecordingUrl={(url) => {
                  onSelect(url);
                  onClose();
                  toast.success(fieldLabel ? `${fieldLabel} alanına eklendi` : "Ses URL'si alana eklendi");
                }}
                applyRecordingLabel={fieldLabel ? `${fieldLabel} — URL kopyala ve ekle` : "Ses URL'sini kopyala ve alana ekle"}
              />
            </div>
          </div>
        ) : (
          <>
            <div className={`mx-5 mt-4 rounded-xl border p-3 space-y-3 shrink-0 ${filterCard}`}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className={`text-[11px] flex items-center gap-1 ${muted}`}><User className="w-3 h-3" /> Agent</Label>
                  <Select value={selectedAgentId || "all"} onValueChange={(v) => setSelectedAgentId(v === "all" ? "" : v)}>
                    <SelectTrigger className={`h-9 ${inputClass}`}><SelectValue placeholder="Tüm agentlar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm agentlar</SelectItem>
                      {agents.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className={`text-[11px] flex items-center gap-1 ${muted}`}><Calendar className="w-3 h-3" /> Başlangıç</Label>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`h-9 ${inputClass}`} />
                </div>
                <div className="space-y-1">
                  <Label className={`text-[11px] flex items-center gap-1 ${muted}`}><Calendar className="w-3 h-3" /> Bitiş</Label>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={`h-9 ${inputClass}`} />
                </div>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Phone className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${muted}`} />
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && search()}
                    placeholder="Numara ile daralt (553… / 0553…)"
                    className={`h-9 pl-8 ${inputClass}`}
                  />
                </div>
                <Button type="button" onClick={search} disabled={loading} className="h-9 shrink-0">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                  Ara
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0 px-5 py-3 space-y-2">
              {loading && (
                <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 animate-spin text-violet-500" /></div>
              )}
              {!loading && items.length === 0 && (
                <p className={`text-sm text-center py-10 ${muted}`}>
                  Numara yazıp Ara&apos;ya basın — veya agent/tarih seçerek arayın
                </p>
              )}
              {!loading && items.map((row) => {
                const active = selectedRow?.id === row.id;
                return (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => setSelectedRow(row)}
                    className={`w-full text-left rounded-xl border p-3 transition-all ${resultCard(active)}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold">#{row.id}</span>
                          <span className={`text-xs ${muted}`}>{row.agent_name || "—"}</span>
                          {row.recording ? (
                            <Badge className="text-[10px] bg-emerald-500/15 text-emerald-600 border-emerald-500/30">Ses var</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-500/40">Ses yok</Badge>
                          )}
                          {active && <Badge className="text-[10px] bg-violet-500/20 text-violet-500 border-violet-500/30">Seçili</Badge>}
                        </div>
                        <div className={`text-xs font-mono ${muted}`}>{row.arayan_numara || "—"} → {row.aranan_numara || "—"}</div>
                        <div className={`text-xs ${muted}`}>{row.tarih || row.call_date} · Süre: {formatCallDuration(row.konusma_suresi)}</div>
                      </div>
                      {active && <Check className="w-5 h-5 text-violet-500 shrink-0" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        <div className={`shrink-0 border-t px-5 py-4 space-y-3 ${isDark ? "border-slate-800 bg-slate-950/60" : "border-gray-200 bg-gray-50/90"}`}>
          {selectedRow ? (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-semibold">Önizleme</span>
                <span className={muted}>#{selectedRow.id}</span>
                <span className={muted}>·</span>
                <span>{selectedRow.agent_name || "—"}</span>
                <span className={muted}>·</span>
                <span className="font-mono">{selectedRow.arayan_numara} → {selectedRow.aranan_numara}</span>
              </div>
              {selectedRow.recording ? (
                <TranscriptAudioPlayer
                  key={selectedRow.id}
                  audioUrl={selectedRow.recording}
                  isDark={isDark}
                  duration={selectedRow.konusma_suresi}
                />
              ) : (
                <p className={`text-sm py-3 text-center ${muted}`}>Bu çağrıda dinlenecek ses kaydı yok</p>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => setSelectedRow(null)}>Seçimi kaldır</Button>
                <Button type="button" variant="outline" onClick={onClose}>İptal</Button>
                <Button type="button" variant="outline" disabled={!selectedRow.recording || pickerDownloading} onClick={handlePickerDownload}>
                  {pickerDownloading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                  İndir
                </Button>
                <Button type="button" onClick={handleConfirm} disabled={!selectedRow.recording}>
                  <Check className="w-4 h-4 mr-1" /> URL&apos;yi {fieldLabel ? "Alana" : "Ekle"}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <p className={`text-xs ${muted}`}>Listeden bir çağrı seçin, sesi dinleyin, ardından ekleyin.</p>
              <Button type="button" variant="outline" onClick={onClose}>Kapat</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CdrRecordingPicker;
